<template>
	<section>
		<v-card elevation="2" rounded="xl" class="presence-card">

			<!-- Toolbar interne -->
			<div class="presence-header">
				<div class="presence-header-left">
					<div class="presence-icon-box">
						<v-icon size="18" color="white">mdi-account-clock</v-icon>
					</div>
					<div>
						<p class="presence-title">Présences en temps réel</p>
						<p class="presence-subtitle">Mise à jour automatique via QR Code</p>
					</div>
				</div>
				<div class="presence-filters-row">
					<v-select
						v-model="selectedSalle"
						:items="salleItems"
						item-text="name"
						item-value="_id"
						outlined
						dense
						hide-details
						class="presence-salle-filter"
						color="blue darken-2"
						prepend-inner-icon="mdi-domain"
						:loading="sallesLoading"
					></v-select>
					<v-text-field
						v-model="search"
						prepend-inner-icon="mdi-magnify"
						placeholder="Rechercher un client..."
						outlined
						dense
						hide-details
						class="presence-search"
						color="blue darken-2"
					></v-text-field>
				</div>
			</div>

			<v-divider></v-divider>

			<v-data-table
				:headers="headers"
				:items="presences"
				:server-items-length="totalItems"
				:options.sync="options"
				:loading="loading"
				loading-text="Chargement en cours..."
				class="presence-table"
				:items-per-page="5"
			:footer-props="{
					'items-per-page-options': [5, 10, 25, 50],
					'items-per-page-text': 'Lignes par page',
					'items-per-page-all-text': 'Tout',
				}"
			>
				<template v-slot:[`item.customer.fullName`]="{ item }">
					<span>{{ item.customer && item.customer.fullName }}</span>
				</template>

				<template v-slot:[`item.salle`]="{ item }">
					<v-chip
						v-if="item.salle && item.salle.name"
						small
						color="#eff6ff"
						text-color="#1565c0"
						class="presence-salle-chip"
					>{{ item.salle.name }}</v-chip>
					<span v-else class="text-caption grey--text">—</span>
				</template>

				<template v-slot:[`item.startAt`]="{ item }">
					<div class="date-cell">
						<span class="date-time">{{ item.startAt.split(' ')[1] }}</span>
						<span class="date-day">{{ item.startAt.split(' ')[0] }}</span>
					</div>
				</template>

				<template v-slot:no-data>
					<div class="no-data">
						<v-icon size="40" color="grey lighten-1">mdi-account-off-outline</v-icon>
						<p>Aucune présence enregistrée</p>
						<v-btn small outlined color="primary" @click="initialize">
							Actualiser
						</v-btn>
					</div>
				</template>

				<template v-slot:[`footer.page-text`]="items">
					{{ items.pageStart }}-{{ items.pageStop }} sur {{ items.itemsLength }}
				</template>
			</v-data-table>
		</v-card>

		<!-- Snackbar retour scan -->
		<v-snackbar v-model="scanSnackbar.show" :color="scanSnackbar.color" timeout="3500" top right>
			{{ scanSnackbar.text }}
			<template v-slot:action>
				<v-btn text small @click="scanSnackbar.show = false">Fermer</v-btn>
			</template>
		</v-snackbar>

		<!-- Dialogue détails client -->
		<v-dialog v-model="dialog" max-width="460px">
			<v-card rounded="xl" class="pa-2">
				<v-card-title class="detail-dialog-title">
					<v-icon color="red darken-2" class="mr-2">mdi-account-circle</v-icon>
					Informations client
					<v-spacer></v-spacer>
					<v-btn icon @click="dialog = false">
						<v-icon>mdi-close</v-icon>
					</v-btn>
				</v-card-title>
				<v-divider></v-divider>
				<v-card-text class="pt-4">
					<v-list dense>
						<v-list-item>
							<v-list-item-icon><v-icon color="blue darken-2">mdi-account</v-icon></v-list-item-icon>
							<v-list-item-content>
								<v-list-item-subtitle>Nom</v-list-item-subtitle>
								<v-list-item-title>{{ clientDetails.name }}</v-list-item-title>
							</v-list-item-content>
						</v-list-item>
						<v-list-item>
							<v-list-item-icon><v-icon color="blue darken-2">mdi-phone</v-icon></v-list-item-icon>
							<v-list-item-content>
								<v-list-item-subtitle>Téléphone</v-list-item-subtitle>
								<v-list-item-title>{{ clientDetails.phone }}</v-list-item-title>
							</v-list-item-content>
						</v-list-item>
						<v-list-item>
							<v-list-item-icon><v-icon color="blue darken-2">mdi-calendar</v-icon></v-list-item-icon>
							<v-list-item-content>
								<v-list-item-subtitle>Âge</v-list-item-subtitle>
								<v-list-item-title>{{ clientDetails.age }} ans</v-list-item-title>
							</v-list-item-content>
						</v-list-item>
						<v-list-item>
							<v-list-item-icon><v-icon color="blue darken-2">mdi-playlist-check</v-icon></v-list-item-icon>
							<v-list-item-content>
								<v-list-item-subtitle>Abonnement</v-list-item-subtitle>
								<v-list-item-title>{{ clientDetails.subscription }} jours — {{ clientDetails.type }}</v-list-item-title>
							</v-list-item-content>
						</v-list-item>
						<v-list-item>
							<v-list-item-icon><v-icon color="red darken-2">mdi-timer-sand</v-icon></v-list-item-icon>
							<v-list-item-content>
								<v-list-item-subtitle>Jours restants</v-list-item-subtitle>
								<v-list-item-title>{{ clientDetails.subscriptionRemaining }} jours</v-list-item-title>
							</v-list-item-content>
						</v-list-item>
					</v-list>
				</v-card-text>
			</v-card>
		</v-dialog>
	</section>
</template>

<script>
import server from "@/config/address";
import services from "@/services";
import io from "socket.io-client";
import moment from "moment";
export default {
	data: () => ({
		socket: null,
		dialog: false,
		clientDetails: {},
		scanSnackbar: { show: false, text: '', color: 'success' },
		loading: false,
		sallesLoading: false,
		search: "",
		searchTimeout: null,
		selectedSalle: null,
		salles: [],
		options: {},
		totalItems: 0,
		headers: [
			{ text: "Client", align: "start", value: "customer.fullName" },
			{ text: "Salle", value: "salle", sortable: false },
			{ text: "Arrivée", value: "startAt" },
		],
		presences: [],
	}),

	computed: {
		salleItems() {
			return [{ _id: null, name: "Toutes les salles" }, ...this.salles];
		},
		currentVenueId() {
			return this.$store.getters.currentVenueId;
		},
	},

	watch: {
		options: {
			handler() { this.initialize(); },
			deep: true,
		},
		search() {
			clearTimeout(this.searchTimeout);
			this.searchTimeout = setTimeout(() => {
				if (this.options.page !== 1) {
					this.options.page = 1;
				} else {
					this.initialize();
				}
			}, 400);
		},
		selectedSalle() {
			if (this.options.page !== 1) {
				this.options.page = 1;
			} else {
				this.initialize();
			}
		},
		currentVenueId(newVal, oldVal) {
			if (oldVal) this.socket.emit("leave-venue", oldVal);
			if (newVal) this.socket.emit("join-venue", newVal);
		},
	},

	created() {
		this.loadSalles();

		// Create the socket here (not in data) so this.$store is accessible.
		this.socket = io(`${server.url}`, { auth: { token: localStorage.getItem('token') || '' } });

		// (Re-)join the venue room on every connection — covers both initial
		// connect and automatic reconnects after a network blip or server restart.
		this.socket.on("connect", () => {
			const vid = this.$store.getters.currentVenueId;
			if (vid) this.socket.emit("join-venue", vid);
		});

		// Show the dialog as soon as the scan is received — before DB save.
		this.socket.on("client-details", (data) => {
			const myVenueId = this.$store.getters.currentVenueId;
			if (!myVenueId) return;
			if (data.salleId && String(data.salleId) !== String(myVenueId)) return;
			this.clientDetails = data;
			this.dialog = true;
		});

		// Presence saved successfully → add to list and confirm.
		this.socket.on("success-scan", (data) => {
			const myVenueId = this.$store.getters.currentVenueId;
			if (!myVenueId) return;
			if (data.salleId && String(data.salleId) !== String(myVenueId)) return;
			const salle = data.salleId ? (this.salles.find(s => String(s._id) === String(data.salleId)) || null) : null;
			this.presences.unshift({
				customer: { fullName: data.name },
				salle,
				startAt: moment().format("YYYY-MM-DD HH:mm"),
			});
			this.scanSnackbar = { show: true, text: `Présence enregistrée — ${data.name}`, color: 'success' };
		});

		// Presence save failed → notify without adding to list.
		this.socket.on("error-scan", (data) => {
			const myVenueId = this.$store.getters.currentVenueId;
			if (!myVenueId) return;
			if (data.salleId && String(data.salleId) !== String(myVenueId)) return;
			const text = data.message || `Échec de l'enregistrement${data.name ? ` — ${data.name}` : ''}`;
			this.scanSnackbar = { show: true, text, color: 'error' };
		});
	},

	mounted() {},

	beforeDestroy() {
		const venueId = this.$store.getters.currentVenueId;
		if (venueId) this.socket.emit("leave-venue", venueId);
		this.socket.disconnect();
	},

	methods: {
		loadSalles() {
			this.sallesLoading = true;
			services.getVenues()
				.then((r) => { this.salles = r.data?.salles || []; this.sallesLoading = false; })
				.catch(() => { this.sallesLoading = false; });
		},
		initialize() {
			this.loading = true;
			const page = this.options.page || 1;
			const limit = this.options.itemsPerPage || 10;
			services
				.getPresencesPaginated({ page, limit, search: this.search, salle: this.selectedSalle || "" })
				.then((r) => {
					this.presences = r.data.presences;
					this.totalItems = (r.data.totalPages ?? 0) * limit;
					this.loading = false;
				})
				.catch((err) => {
					console.log(err);
					this.loading = false;
				});
		},
		initials(name) {
			if (!name) return "?";
			return name
				.split(" ")
				.slice(0, 2)
				.map((n) => n[0])
				.join("")
				.toUpperCase();
		},
	},
};
</script>

<style scoped>
.presence-card {
	font-family: Montserrat, sans-serif;
}

/* ─── Header ─── */

.presence-header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 18px 20px 14px;
	gap: 16px;
	flex-wrap: wrap;
}

.presence-header-left {
	display: flex;
	align-items: center;
	gap: 14px;
	flex: 1;
	min-width: 200px;
}

.presence-icon-box {
	width: 38px;
	height: 38px;
	border-radius: 10px;
	background: #c62828;
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
}

.presence-title {
	font-size: 0.92rem;
	font-weight: 700;
	color: #111827;
	margin: 0 0 2px;
}

.presence-subtitle {
	font-size: 0.72rem;
	color: #9ca3af;
	margin: 0;
}

.presence-filters-row {
	display: flex;
	align-items: center;
	gap: 10px;
	flex-wrap: wrap;
}

.presence-salle-filter {
	max-width: 200px;
	min-width: 160px;
}

.presence-search {
	max-width: 240px;
}

/* ─── Table ─── */

.presence-table {
	font-family: Montserrat, sans-serif;
}

.presence-salle-chip {
	font-size: 0.78rem !important;
	font-family: Montserrat, sans-serif;
	font-weight: 600;
}

.date-cell {
	display: flex;
	flex-direction: column;
	gap: 1px;
}

.date-time {
	font-size: 0.83rem;
	font-weight: 600;
	color: #111827;
	font-family: Montserrat, sans-serif;
}

.date-day {
	font-size: 0.7rem;
	color: #9ca3af;
	font-family: Montserrat, sans-serif;
}

/* ─── Pas de données ─── */

.no-data {
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 10px;
	padding: 32px 0;
	color: #9ca3af;
}

.no-data p {
	font-size: 0.85rem;
	margin: 0;
}

/* ─── Dialogue ─── */

.detail-dialog-title {
	font-size: 1rem;
	font-weight: 700;
}
</style>
